import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { useThemeColors } from '../context/ThemeContext';
import { dsv2, dsv2Light } from '../styles/designSystemV2';
import { MailWarning, X } from 'lucide-react';
import EmailCodeModal from './EmailCodeModal';
import { zLayer } from '../styles/zIndex';

// Banner NO bloqueante: nudge para que el usuario verifique su correo. Solo
// aparece para cuentas de email/contraseña sin verificar (Google ya viene
// verificado). Descartable por sesión; reaparece al recargar hasta verificar.
//
// ─── POR QUÉ SOLO EN ESTAS DOS RUTAS (2026-08-07) ───────────────────────────
//
// El banner es `position: fixed` a 92px del fondo, y esos 92px asumen que abajo
// solo está el BottomNav del móvil. No es cierto: varias pantallas tienen su
// PROPIA barra fija en esa misma franja, y como el banner va en z-index 8900 les
// gana. Medido con elementFromPoint en producción, BLOQUEABA de verdad (no
// "estorbaba"): el botón "Siguiente" de /checkout —en móvil Y en escritorio—,
// el campo de kilometraje de /vehicles/new, un campo de /profile/edit y la barra
// de escritura de /diagnose (Diagnose.js la fija en el mismo bottom: 92px).
//
// Le sale justo a quien acaba de registrarse, o sea a todo el que entre por la
// campaña, y le corta el paso en mitad de una compra.
//
// La lista es BLANCA y no negra a propósito: enumerar las pantallas conflictivas
// es una carrera que se pierde (cada barra fija nueva vuelve a romperlo en
// silencio). Aquí solo viven rutas sin barra inferior propia.
//
// El aviso no se pierde: /home es el aterrizaje después de entrar, así que todo
// el mundo lo ve, e Inbox ofrece el mismo EmailCodeModal como segunda vía.
//
// Solo `/home`, y de una sola ruta se puede responder con certeza. Se probó
// también `/dashboard` y se quitó: aunque no tiene barra de acciones, el banner
// caía encima del enlace de "Ayuda / Preguntas frecuentes" y lo dejaba
// inalcanzable. Un elemento `fixed` sobre contenido que scrollea SIEMPRE tapa
// algo; la única garantía es medir ruta por ruta.
//
// ⚠️ Antes de añadir una ruta: (1) comprobar que EXISTE en App.js —el perfil es
// `/dashboard`, no `/profile`—, y (2) medir con elementFromPoint, en móvil Y en
// escritorio, que no deja ningún control debajo. Sin medirlo, no se añade.
const RUTAS_CON_BANNER = ['/home'];

export default function VerifyEmailBanner() {
  const { t } = useTranslation();
  const location = useLocation();
  const { user, userProfile, refreshProfile } = useAuth();
  const { showToast } = useToast();
  const { isDark } = useThemeColors();
  const tk = isDark ? dsv2 : dsv2Light;

  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const needsVerify = !!user && user.emailVerified === false && !userProfile?.emailVerified;
  const rutaPermitida = RUTAS_CON_BANNER.includes(location.pathname);
  if (!needsVerify || dismissed || !rutaPermitida) return null;

  const onSuccess = async () => {
    setOpen(false);
    try { await user.reload(); } catch { /* best-effort */ }
    try { await refreshProfile(); } catch { /* best-effort */ }
    showToast(t('verify.successToast'), 'success');
  };

  return (
    <>
      <div style={{
        position: 'fixed', left: '50%', transform: 'translateX(-50%)',
        bottom: 'calc(92px + env(safe-area-inset-bottom))',
        zIndex: zLayer.verifyBanner, width: 'calc(100% - 24px)', maxWidth: 460,
        background: tk.surface, border: `1px solid ${tk.outline}`,
        borderRadius: 14, boxShadow: tk.shadow?.md || '0 8px 24px rgba(0,0,0,0.18)',
        padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <div style={{
          width: 38, height: 38, borderRadius: 11, flex: 'none',
          background: tk.amberSoft, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <MailWarning size={20} color={tk.amber} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13.5, fontWeight: 700, color: tk.text }}>{t('verify.bannerTitle')}</div>
          <div style={{ fontSize: 12, color: tk.textMuted, lineHeight: 1.4 }}>{t('verify.bannerBody')}</div>
        </div>
        <button
          onClick={() => setOpen(true)}
          style={{
            flex: 'none', padding: '8px 14px', borderRadius: 10, border: 'none',
            background: tk.red, color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer',
          }}
        >
          {t('verify.bannerCta')}
        </button>
        <button onClick={() => setDismissed(true)} style={{ flex: 'none', background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}>
          <X size={16} color={tk.textMuted} />
        </button>
      </div>

      {open && (
        <EmailCodeModal
          purpose="verify_email"
          title={t('verify.modalTitle')}
          description={t('verify.modalDesc')}
          onSuccess={onSuccess}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}
